import { Fragment } from "react";
import { Location as TQSLocation } from "../../models/location";
import { User } from "../../models/user";
import CopyButton from "./CopyButton";

const INCLUDED_USER_TYPES = ["manager", "manager/rep"];
/*
 * A location's ops managers are the managers of its assigned reps, so there can
 * be several. locations.regionalManager only stores the most common one, so it
 * is a fallback for reps that have no manager of their own.
 */
export function getOpsManagers(
  location: TQSLocation | null | undefined,
): User[] {
  if (location?.opsManagers?.length) {
    return location.opsManagers;
  }

  const managers = (location?.agreement?.assignedReps ?? [])
    .filter((rep) => rep?.representative)
    .filter((rep) => !rep.endTime || new Date(rep.endTime) >= new Date())
    .map((rep) => rep.representative.manager)
    .filter(
      (manager): manager is User =>
        !!manager && typeof manager === "object" && !!manager.id,
    )
    .filter(
      (manager, index, array) =>
        array.findIndex((m) => m.id === manager.id) === index,
    ).filter((manager) => {
      return INCLUDED_USER_TYPES.includes(manager.type);
    });
  if (managers.length > 0) {
    return managers;
  }

  const regionalManager = location?.regionalManager;
  return regionalManager?.id && INCLUDED_USER_TYPES.includes(regionalManager.type) ? [regionalManager] : [];
}

function formatNumber(number: string | undefined): string {
  if (number === undefined || number === null) return "N/A";

  const cleaned = number.replace(/\D/g, "");
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
}

function PhoneButton({ number, name }: { number: string; name: string }) {
  return (
    // The number is not shown inline, so the tooltip carries it. Left, not
    // bottom: .collapse is overflow:hidden and would clip it when closed.
    <CopyButton
      text={number}
      tooltip={formatNumber(number)}
      ariaLabel={`Copy phone number for ${name}`}
      tooltipPosition="left"
      className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#3a7bfc] text-white"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="h-[14px] w-[14px]"
      >
        <path
          fillRule="evenodd"
          d="M4.848 2.771A49.144 49.144 0 0 1 12 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 0 1-3.476.383.39.39 0 0 0-.297.17l-2.755 4.133a.75.75 0 0 1-1.248 0l-2.755-4.133a.39.39 0 0 0-.297-.17 48.9 48.9 0 0 1-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97ZM6.75 8.25a.75.75 0 0 1 .75-.75h9a.75.75 0 0 1 0 1.5h-9a.75.75 0 0 1-.75-.75Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H7.5Z"
          clipRule="evenodd"
        />
      </svg>
    </CopyButton>
  );
}

export default function OpsManagers({
  location,
}: {
  location: TQSLocation | null | undefined;
}) {
  const managers = getOpsManagers(location);

  if (managers.length === 0) {
    return null;
  }
  return (
    // z-10 clears the collapse's invisible checkbox, so clicking an icon does
    // not also toggle the accordion. Grid, not flex rows, so a manager with no
    // phone still lines up with the one above.
    <div className="relative z-10 ml-auto grid grid-cols-[auto_auto_auto_auto] items-center gap-x-2 gap-y-1 pl-4">
      {managers.map((manager) => {
        const phone = manager.phone ?? manager.cell;
        const name = `${manager.firstName} ${manager.lastName}`;

        return (
          <Fragment key={manager.id}>
            <img
              className="h-7 w-7 rounded-full"
              src={
                (manager as any)?.photoAttachment?.downloadURL
                  ? (manager as any)?.photoAttachment?.downloadURL
                  : "/dashboard/avatar.png"
              }
              alt=""
            />
            <p className="whitespace-nowrap text-sm font-medium text-tqsBlack">
              {name}
            </p>
            <p className="whitespace-nowrap text-xs text-gray-500">
              ops manager
            </p>
            <div className="flex items-center gap-1">
              {manager.email && (
                <a
                  href={`mailto:${manager.email}`}
                  className="tooltip tooltip-left flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#73cffd] text-white"
                  data-tip={manager.email}
                  aria-label={`Email ${name}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="h-[14px] w-[14px]"
                  >
                    <path d="M1.5 8.67v8.58a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3V8.67l-8.928 5.493a3 3 0 0 1-3.144 0L1.5 8.67Z" />
                    <path d="M22.5 6.908V6.75a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3v.158l9.714 5.978a1.5 1.5 0 0 0 1.572 0L22.5 6.908Z" />
                  </svg>
                </a>
              )}
              {phone && <PhoneButton number={phone} name={name} />}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
